import { useMemo } from 'react';
import { API_CONFIG, EarthscaleLayerConfig } from '../lib/apiConfig';
import type { GeoJSON as GeoJSONType } from '../lib/types';

export interface CanopyHeightLayer {
  /** Canonical state name — doubles as a stable React key. */
  state: string;
  /** Leaflet-ready tile template with {z}/{x}/{y} placeholders. */
  tileUrl: string;
  /**
   * LatLngBounds literal for Leaflet's TileLayer `bounds` prop. Leaflet skips
   * tile requests that don't intersect this box, so a neighbor dataset never
   * gets asked for tiles that are entirely outside its state.
   */
  bounds: [[number, number], [number, number]];
}

interface UseCanopyHeightOverlayResult {
  /**
   * One entry per state dataset whose bbox overlaps the shed. Typically a
   * single entry; cross-border sheds (e.g. Alabama Piedmont reaching into
   * Georgia) return multiple so tiles from each state stitch together. The
   * mill's own state is rendered **last** so it paints on top of any
   * neighbor datasets — this keeps out-of-extent dark-zero pixels from a
   * neighbor dataset from masking real canopy data in the primary state.
   * Empty when the layer isn't enabled or no configured state matches.
   */
  layers: CanopyHeightLayer[];
  /** True when at least one layer resolved. */
  isAvailable: boolean;
  /**
   * Union rescale used for every layer's tile URL — [0, max(involved maxes)]
   * in meters. Forcing one rescale across stacked datasets keeps the viridis
   * ramp continuous at state borders. Drives the legend.
   */
  rescale: [number, number] | null;
  /**
   * Effective map max-zoom while this layer is active — the most restrictive
   * (min) native max across all involved state datasets. `null` when the
   * layer isn't available. Callers pass this into `BasemapZoomClamp` so the
   * user can't zoom past the source resolution.
   */
  maxZoom: number | null;
}

type BBox = [west: number, south: number, east: number, north: number];

const polygonBbox = (polygon: GeoJSONType.Polygon): BBox | null => {
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  for (const ring of polygon.coordinates) {
    for (const [lng, lat] of ring) {
      if (lng < west) west = lng;
      if (lng > east) east = lng;
      if (lat < south) south = lat;
      if (lat > north) north = lat;
    }
  }
  if (!Number.isFinite(west)) return null;
  return [west, south, east, north];
};

const bboxOverlaps = (a: BBox, b: BBox): boolean =>
  a[0] < b[2] && a[2] > b[0] && a[1] < b[3] && a[3] > b[1];

const buildTileUrl = (cfg: EarthscaleLayerConfig, rescale: [number, number]): string => {
  const params = new URLSearchParams({
    api_key: API_CONFIG.earthscale.apiKey,
    variables: cfg.variable,
    rescale: `${rescale[0]},${rescale[1]}`,
    colormap_name: 'viridis',
    reverse_colormap: 'false',
    // Variable-prefixed `nodata` (per Earthscale fast-tiler docs) — treats
    // the value-0 fill each dataset returns outside its real-data extent as
    // transparent, so border tiles don't paint dark-purple rectangles over a
    // neighbor state's real canopy. Genuine 0 m canopy pixels also go
    // transparent, revealing the basemap beneath — acceptable, since those
    // areas would render at the darkest end of the viridis ramp anyway.
    nodata: `${cfg.variable}:0`,
  });
  return `${API_CONFIG.earthscale.tileHost}/v1/raster/${cfg.datasetVersionId}/tiles/{z}/{x}/{y}.webp?${params.toString()}`;
};

const cfgBounds = (cfg: EarthscaleLayerConfig): [[number, number], [number, number]] => {
  const [west, south, east, north] = cfg.bbox;
  return [
    [south, west],
    [north, east],
  ];
};

/**
 * Resolves Earthscale canopy-height tile URLs for every state dataset that
 * overlaps the mill's supply shed. The mill's own state is always included
 * when configured, even if the shed geometry isn't loaded yet. Returns empty
 * (without error) when the layer isn't enabled or the state has no dataset.
 */
export const useCanopyHeightOverlay = (
  state: string | null,
  enabled: boolean = true,
  boundary: GeoJSONType.Polygon | null = null,
): UseCanopyHeightOverlayResult => {
  return useMemo(() => {
    if (!enabled || !state) {
      return { layers: [], isAvailable: false, rescale: null, maxZoom: null };
    }
    const allConfigs = API_CONFIG.earthscale.canopyHeight;
    const primary = allConfigs[state];
    if (!primary) {
      return { layers: [], isAvailable: false, rescale: null, maxZoom: null };
    }

    // Collect every configured state whose bbox the shed touches. Bbox
    // overlap is conservative — it may pick up a neighbor even when the shed
    // geometry itself doesn't cross — but stacked extras cost nothing since
    // tile-level `bounds` + shed clip + `nodata=0` suppress anything outside
    // actual coverage.
    const neighbors: [string, EarthscaleLayerConfig][] = [];
    if (boundary) {
      const shedBbox = polygonBbox(boundary);
      if (shedBbox) {
        for (const [otherState, cfg] of Object.entries(allConfigs)) {
          if (!cfg || otherState === state) continue;
          if (bboxOverlaps(shedBbox, cfg.bbox)) {
            neighbors.push([otherState, cfg]);
          }
        }
      }
    }

    // Union rescale: max of all involved states' maxes. A single-state shed
    // keeps its tuned range; a cross-border shed widens to the larger so both
    // datasets map the same canopy height to the same viridis color.
    const maxRescale = Math.max(
      primary.rescale[1],
      ...neighbors.map(([, c]) => c.rescale[1]),
    );
    const unionRescale: [number, number] = [0, maxRescale];

    // Render order matters: neighbors first, then the primary state on top.
    // That way, if a neighbor's tiles leak a strip of nodata/dark-fill into
    // the primary state, the primary state's real tiles paint over them.
    const ordered: [string, EarthscaleLayerConfig][] = [
      ...neighbors,
      [state, primary],
    ];
    const layers: CanopyHeightLayer[] = ordered.map(([layerState, cfg]) => ({
      state: layerState,
      tileUrl: buildTileUrl(cfg, unionRescale),
      bounds: cfgBounds(cfg),
    }));

    // Most restrictive native max across involved datasets — if a shed
    // spans states with different source resolutions, we cap at the
    // lower-resolution one to avoid one side going crisp and the other
    // going blurry past its native zoom.
    const maxZoom = Math.min(
      primary.maxZoom,
      ...neighbors.map(([, c]) => c.maxZoom),
    );

    return { layers, isAvailable: true, rescale: unionRescale, maxZoom };
  }, [state, enabled, boundary]);
};
